import PaginationContext from '@jetshop/core/components/Pagination/PaginationContext';
import t from '@jetshop/intl';
import theme from '@jetshop/ui/utils/theme';
import { Pagination, PaginationBehaviour } from '@jetshop/ui/Pagination';
import { PaginationWrapper } from '@jetshop/ui/Pagination/Pagination';
import get from 'lodash.get';
import React, { useContext } from 'react';
import styled, { css } from 'react-emotion';
import posed from 'react-pose';
import CategoryHeader from '../CategoryPage/CategoryHeader';
import MaxWidth from '../Layout/MaxWidth';
import CategoryHeaderLoadingState from './CategoryHeaderLoadingState';
import Filters from './Filters';
import ProductGrid from './ProductGrid';
import HelloRetailRecommendations from '../HelloRetail';
import ChannelContext from '@jetshop/core/components/ChannelContext';

const RecommendedWrapper = styled(MaxWidth)`
  padding: 0 !important;
  & > div {
    margin-top: 0;

    .hello-retail-recommendations {
      padding-top: 0;
      padding-bottom: 0;
      & > div h2 {
        margin-top: 2rem;
      }
    }
  }
`;

const Container = styled(MaxWidth)`
  padding-top: 2rem;
  ${({ theme }) => theme.above.sm} {
    padding-top: 2rem;
  }
`;

const StyledPaginationWrapper = styled(PaginationWrapper)`
  margin-bottom: 2rem;
  margin-top: 2rem;
  justify-content: center;
  > div {
    justify-content: center;
    ${theme('below.md')} {
      justify-content: space-between;
    }
  }
  ${theme('below.md')} {
    margin-bottom: 1rem;
  }
  ${theme('below.sm')} {
    margin-top: 1rem;
  }
  a,
  div div {
    text-decoration: none;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    font-size: 12px;
    justify-content: center;
    ${theme('below.sm')} {
    }
  }
  a {
    color: ${theme('colors.white')};
    background: ${theme('colors.primary')};
    border: 1px solid ${theme('colors.primary')};
    padding: 0.5rem 0;
    transition: 0.2s ease;
    width: 130px;
    text-align: center;
    margin: 0 20px;
    font-weight: bold;
    ${theme('below.lg')} {
      width: 110px;
      padding: 8px 0;
      margin: 0;
      letter-spacing: 0.5px;
    }
    ${theme('below.sm')} {
    }
    :hover {
      background: ${theme('colors.white')};
      color: ${theme('colors.black')};
    }
  }
  a[data-disabled='true'] {
    background: ${theme('colors.white')};
    border-color: ${theme('colors.grey')};
    color: ${theme('colors.black')};
    cursor: not-allowed;
    opacity: 0.4;
    font-weight: normal;
    ${theme('below.sm')} {
    }
  }
`;

const CategoryWrapper = styled('div')``;

const CategoryContentWrapper = styled('div')`
  background: #f7f7f7;
  margin-bottom: -2rem;
`;
const CategoryContent = styled('div')`
  max-width: 900px;
  padding: 2rem;
  margin: 0 auto;

  img {
    max-width: 100%;
    height: auto;
    margin-bottom: 30px;
  }
  p {
    margin-bottom: 2rem;
    font-size: 13px;
  }

  a {
    font-weight: bold;
    color: black;
    text-decoration: none;
  }
  h1,
  h2,
  h3,
  h4 {
    line-height: 1.2;
  }
`;

// Expand the filters out from 0 height to full height once they load
const FilterAnimation = posed.div({
  closed: {
    height: 0,
    opacity: 0,
    overflow: 'hidden',
  },
  open: {
    height: 'auto',
    opacity: 1,
    duration: 600,
    applyAtEnd: { overflow: 'visible' },
  },
});

// Fade in the product grid once it loads
const ContainerAnimation = posed.div({
  closed: {
    opacity: 0,
  },
  open: { opacity: 1 },
});

// This wraps the filters and the product grid and staggers their animation.
// So the FilterAnimation will run first, and then 200ms later the
// ContainerAnimation will run.
const StaggerAnimation = posed.div({
  open: { staggerChildren: 200 },
});

const Category = ({ category, cacheHit, result }) => {
  const catContent = category?.content ?? null;
  var firstParagraph = null;
  if (catContent && catContent.indexOf('<p') > -1) {
    firstParagraph = catContent.split('</p>')[0];
  }

  const { selectedChannel } = useContext(ChannelContext);
  const parents = get(result.data, 'route.parents');

  let categoryParents = null;
  if (parents) {
    categoryParents = [...parents];
    const currentCategory = {
      id: '1' + category.breadcrumbText,
      object: { breadcrumbText: category.breadcrumbText },
    };
    categoryParents.unshift(currentCategory);
  }
  const helloRetailRecommendationsId1 =
    selectedChannel.id === 1
      ? 'k5fbbc43ba5a4ee1229620b5c'
      : 'k5fbbc1a1a5a4ee1229608354';

  return (
    <>
      <CategoryWrapper>
        <>
          {!cacheHit ? (
            // Show a dummy header if we don't have cached data
            <CategoryHeaderLoadingState />
          ) : (
            <CategoryHeader
              category={category}
              parents={get(result.data, 'route.parents')}
              firstParagraph={firstParagraph}
            />
          )}
          {category.products.totalResults >= 10 && (
            <RecommendedWrapper>
              <HelloRetailRecommendations
                recommendationsList={[
                  {
                    title: 'Top products',
                    recommendationId: helloRetailRecommendationsId1,
                    slider: true,
                    parents: categoryParents ?? parents,
                  },
                ]}
              />
            </RecommendedWrapper>
          )}
          <Container>
            <StaggerAnimation pose={cacheHit && 'open'}>
              <FilterAnimation pose={cacheHit ? 'open' : 'closed'}>
                <Filters
                  filters={category.products.filters || []}
                  sortOrders={category.products.sortOrders || []}
                  result={result}
                  category={category}
                  total={category.products.totalResults}
                />
              </FilterAnimation>
              <ContainerAnimation pose={cacheHit ? 'open' : 'closed'}>
                <ProductGrid
                  sliderMobile={false}
                  products={category.products.result}
                  listName={category.name}
                  categoryPath={
                    category.isDynamic ? null : get(result.data, 'route')
                  }
                  loading={result.loading}
                  imageSizes={[1 / 2, 1 / 2, 1 / 3, 1 / 4]}
                />
              </ContainerAnimation>
            </StaggerAnimation>

            <PaginationContext.Consumer>
              {(props) => (
                <PaginationBehaviour
                  {...props}
                  total={category.products.totalResults}
                >
                  {(props) => (
                    <StyledPaginationWrapper>
                      <Pagination {...props} />
                    </StyledPaginationWrapper>
                  )}
                </PaginationBehaviour>
              )}
            </PaginationContext.Consumer>
          </Container>
        </>
      </CategoryWrapper>
      {category?.content && (
        <CategoryContentWrapper>
          <CategoryContent
            id="full-category-content"
            dangerouslySetInnerHTML={{
              __html: category?.content,
            }}
          />
        </CategoryContentWrapper>
      )}
    </>
  );
};

export default Category;
